import { apiClient } from '../lib/apiClient';

/**
 * Budget module: what the tank costs (salt, food, filter media, electricity,
 * …), monthly and yearly spend, the comparison with the previous year and the
 * year-end forecast, recurring costs. Amounts are ×100 in the user's currency
 * (Settings → General).
 */
export const BUDGET_CATEGORIES = [
  'salt',
  'food',
  'filter_media',
  'supplements',
  'electricity',
  'water',
  'livestock',
  'equipment',
  'other',
] as const;
export type BudgetCategory = (typeof BUDGET_CATEGORIES)[number];

export type CategoryCents = Readonly<Record<BudgetCategory, number>>;

export interface BudgetExpense {
  readonly id: string;
  readonly aquariumId: string;
  readonly category: BudgetCategory;
  readonly amountCents: number;
  /** YYYY-MM-DD */
  readonly spentOn: string;
  readonly note: string | null;
  /** The recurring cost this entry was booked from; null for a one-off expense. */
  readonly recurringId: string | null;
  readonly createdAt: string;
}

/** A recurring cost: every `intervalMonths` months from `startOn` (until `endOn`), booked as expenses up to today. */
export interface RecurringExpense {
  readonly id: string;
  readonly aquariumId: string;
  readonly category: BudgetCategory;
  readonly amountCents: number;
  readonly note: string | null;
  readonly intervalMonths: number;
  readonly startOn: string;
  readonly endOn: string | null;
  /** The next date it will be booked; null once it has ended. */
  readonly nextOn: string | null;
  readonly createdAt: string;
}

export interface RecurringInput {
  category: BudgetCategory;
  amount_cents: number;
  note: string | null;
  interval_months: number;
  start_on: string;
  end_on: string | null;
}

export interface MonthSpend {
  readonly month: number;
  readonly totalCents: number;
  readonly byCategory: CategoryCents;
  readonly projectedCents: number | null;
  readonly projectedByCategory: CategoryCents | null;
}

export interface CategorySpend {
  readonly category: BudgetCategory;
  readonly totalCents: number;
  readonly previousTotalCents: number;
  readonly previousSamePeriodCents: number;
  readonly monthlyRateCents: number | null;
  readonly projectedTotalCents: number | null;
}

export interface BudgetSummary {
  readonly year: number;
  readonly asOf: string;
  readonly isCurrentYear: boolean;
  readonly months: MonthSpend[];
  readonly previousMonths: MonthSpend[];
  readonly categories: CategorySpend[];
  readonly totals: {
    readonly totalCents: number;
    readonly previousTotalCents: number;
    readonly previousSamePeriodCents: number;
    readonly projectedTotalCents: number | null;
  };
  readonly forecastBasisMonths: number | null;
  readonly availableYears: number[];
}

export interface ExpenseInput {
  category: BudgetCategory;
  amount_cents: number;
  spent_on: string;
  note: string | null;
}

const base = (aquariumId: string) => `/api/v1/aquariums/${aquariumId}/budget`;

export const budgetKey = (aquariumId: string) => ['budget', aquariumId] as const;
export const budgetSummaryKey = (aquariumId: string, year: number) => ['budget', aquariumId, 'summary', year] as const;
export const budgetExpensesKey = (aquariumId: string, year: number) => ['budget', aquariumId, 'expenses', year] as const;
export const budgetRecurringKey = (aquariumId: string) => ['budget', aquariumId, 'recurring'] as const;

export async function getBudgetSummary(aquariumId: string, year: number, asOf: string): Promise<BudgetSummary> {
  const query = new URLSearchParams({ year: String(year), as_of: asOf });
  const res = await apiClient.get<{ data: BudgetSummary }>(`${base(aquariumId)}/summary?${query.toString()}`);
  return res.data;
}

/** `asOf`: the local date — recurring costs are booked up to it before listing. */
export async function listExpenses(aquariumId: string, year: number, asOf: string): Promise<BudgetExpense[]> {
  const query = new URLSearchParams({ year: String(year), as_of: asOf });
  const res = await apiClient.get<{ data: BudgetExpense[] }>(`${base(aquariumId)}/expenses?${query.toString()}`);
  return res.data;
}

export async function createExpense(aquariumId: string, input: ExpenseInput): Promise<BudgetExpense> {
  const res = await apiClient.post<{ data: BudgetExpense }>(`${base(aquariumId)}/expenses`, input);
  return res.data;
}

export async function updateExpense(aquariumId: string, id: string, input: Partial<ExpenseInput>): Promise<BudgetExpense> {
  const res = await apiClient.patch<{ data: BudgetExpense }>(`${base(aquariumId)}/expenses/${encodeURIComponent(id)}`, input);
  return res.data;
}

export async function deleteExpense(aquariumId: string, id: string): Promise<void> {
  await apiClient.delete(`${base(aquariumId)}/expenses/${encodeURIComponent(id)}`);
}

export async function listRecurring(aquariumId: string): Promise<RecurringExpense[]> {
  const res = await apiClient.get<{ data: RecurringExpense[] }>(`${base(aquariumId)}/recurring`);
  return res.data;
}

export async function createRecurring(aquariumId: string, input: RecurringInput): Promise<RecurringExpense> {
  const res = await apiClient.post<{ data: RecurringExpense }>(`${base(aquariumId)}/recurring`, input);
  return res.data;
}

/** Applies to future bookings; entries already booked keep their amount. */
export async function updateRecurring(aquariumId: string, id: string, input: Partial<RecurringInput>): Promise<RecurringExpense> {
  const res = await apiClient.patch<{ data: RecurringExpense }>(`${base(aquariumId)}/recurring/${encodeURIComponent(id)}`, input);
  return res.data;
}

/** Entries already booked stay as one-off expenses. */
export async function deleteRecurring(aquariumId: string, id: string): Promise<void> {
  await apiClient.delete(`${base(aquariumId)}/recurring/${encodeURIComponent(id)}`);
}
